import type { PoiValidationError } from '@/features/poi/poi-form-model/types'
import type {
  IPoiPendingImage,
  IPoiTranslation,
  PoiBasicInfoState,
} from '@/shared/api/types/poi-types'
import {
  hasErroredImages,
  hasUploadingImages,
  successfulImageUrls,
} from '@/features/poi/poi-form-model/images'
import { hasRequiredTranslations } from '@/shared/utils/language-utils'

/** The POI must be tied to a tenant destination, have a type and a category. */
export function validatePoiBasicInfo(
  basicInfo: PoiBasicInfoState,
  tenantDestinationId: string,
): PoiValidationError | null {
  if (!tenantDestinationId) {
    return { key: 'poi.destinationRequired' }
  }
  if (!basicInfo.type) {
    return { key: 'poi.selectType' }
  }
  if (!basicInfo.category.trim()) {
    return { key: 'poi.categoryRequired' }
  }
  return null
}

/** Saving is blocked while an upload is running or failed. */
export function validatePoiImageUploads(images: IPoiPendingImage[]): PoiValidationError | null {
  if (hasUploadingImages(images)) {
    return { key: 'poi.media.waitForUpload' }
  }
  if (hasErroredImages(images)) {
    return { key: 'poi.media.resolveUploadErrors' }
  }
  return null
}

/** At least one successfully uploaded image is mandatory. */
export function validatePoiImageCount(images: IPoiPendingImage[]): PoiValidationError | null {
  return successfulImageUrls(images).length < 1 ? { key: 'poi.imagesRequired' } : null
}

/** Media section rules: uploads settled, then at least one image. */
export function validatePoiImages(images: IPoiPendingImage[]): PoiValidationError | null {
  return validatePoiImageUploads(images) ?? validatePoiImageCount(images)
}

/** Every language needs a name, a description and the audio script. */
export function validatePoiTranslations(
  translations: IPoiTranslation[],
): PoiValidationError | null {
  return hasRequiredTranslations(translations, ['name', 'description', 'audioContent'])
    ? null
    : { key: 'poi.allLanguagesTranslationRequired' }
}

export interface PoiSubmissionInput {
  basicInfo: PoiBasicInfoState
  tenantDestinationId: string
  images: IPoiPendingImage[]
  /** Translations already trimmed by `normalizePoiTranslations`. */
  translations: IPoiTranslation[]
}

/**
 * VI: Validate toàn bộ form và trả lỗi đầu tiên để chỉ hiện một toast. Lỗi upload ưu tiên trước vì nó tự thay đổi trong lúc user còn đang nhập.
 * EN: Validates the whole create form and returns the first failure, so a single toast is shown. Upload problems come first because they change on their own while the user is still filling in the rest.
 */
export function validatePoiSubmission(input: PoiSubmissionInput): PoiValidationError | null {
  return (
    validatePoiImageUploads(input.images) ??
    validatePoiBasicInfo(input.basicInfo, input.tenantDestinationId) ??
    validatePoiImageCount(input.images) ??
    validatePoiTranslations(input.translations)
  )
}
